import React, { useMemo, useState, useCallback } from "react";
import { Button, Col, Form, Modal, Row, Table } from "react-bootstrap";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../../../_helper/CurrencyContext";
import { useDisableScrollOnFocus } from "../../../../Hooks/useDisableScrollOnFocus";
import { formatAmount, PAYMENT_METHOD_OPTIONS } from "../../../PatientAccounts/utils/patientAccountUtils";
import { masterPharmaDoctorsAPI } from "../../../PharmaDoctors/PharmaDoctorService";
import {
	EMPTY_DOCTOR_FORM,
	buildDoctorSelectOption,
	hasDoctorFormInput,
	mapMasterDoctorToForm,
	sanitizeDoctorName,
} from "../utils/doctorFormUtils";
import { formatDate } from "../utils/dateUtils";
import {
	formatMasterOptionLabel,
	getPrescriptionBillingFlags,
} from "../utils/billingUtils";
import { toMoneyNumber } from "../../../../utils/financeEngine";
import "./BillingPaymentModal.scss";
import {
	BillingAmountCell,
	BillingLineAmountInput,
	BillingLineQuantityInput,
	BillingMasterSelect,
	BillingPriorPaymentSummaryRows,
	BillingSectionDivider,
	CollapsibleSection,
	SourceBadge,
	SuggestedInvestigationsBlock,
} from "./billingModalParts";
import {
	BillingPharmacyMedicinesTable,
	BillingPharmacySummaryBreakdown,
	usePharmacyMedicinesDisplayModel,
} from "./BillingPharmacySection";
import {
	BillingInvestigationsAdjustments,
	BillingInvestigationsSummaryBreakdown,
	BillingServicesAdjustments,
	BillingServicesSummaryBreakdown,
} from "./BillingServicesSection";

const buildNewDoctorOption = (name) => {
	const trimmed = sanitizeDoctorName(name);
	if (!trimmed) return null;
	return {
		value: `new:${trimmed}`,
		label: trimmed,
		isNew: true,
	};
};
const BillingPaymentModal = ({
	show,
	onHide,
	activePrescription,
	selectedPatient,
	serviceItems,
	servicesBillingTotals,
	servicesDiscountType,
	servicesDiscountValue,
	servicesTaxConfig,
	onServicesDiscountChange,
	onServicesTaxChange,
	investigationsBillingTotals,
	investigationsDiscountType,
	investigationsDiscountValue,
	investigationsTaxConfig,
	onInvestigationsDiscountChange,
	onInvestigationsTaxChange,
	investigationItems,
	suggestedInvestigations = [],
	searchGeneralServices,
	searchLabTests,
	cart = [],
	priorPharmacyItems = [],
	itemTaxes = {},
	totals = {},
	discountType = "Percentage",
	discountValue = 0,
	posTaxType = "gst",
	pharmacyStateCode = "",
	patientStateCode = "",
	showPosTax = true,
	billingSummary,
	billingBaseCurrency,
	defaultCurrency,
	convertCurrency,
	payment,
	onPaymentChange,
	errors,
	submitted,
	sectionOpen,
	onToggleSection,
	updateInvestigationAmount,
	updateInvestigationQuantity,
	updateServiceAmount,
	updateServiceQuantity,
	onRemoveInvestigation,
	onRemoveServiceItem,
	onAddServiceRow,
	onSelectServiceForRow,
	onAddInvestigationRow,
	onSelectInvestigationForRow,
	paymentBalance,
	onCollectPayment,
	onCollectAndMarkPaid,
	isSubmitting,
	isLoading,
	doctorId,
	doctorForm = {},
	onDoctorFormChange,
	onDoctorIdChange,
	selectedDoctorOption,
	onSelectedDoctorOptionChange,
	onClearDoctorSelection,
	onAddDoctorToMaster,
	addingDoctor = false,
}) => {
	const { t } = useTranslation();
	const { getCurrencySymbol, allowedCurrencies } = useCurrency();
	const disableScrollProps = useDisableScrollOnFocus();
	const [pharmacyBreakdownOpen, setPharmacyBreakdownOpen] = useState(true);
	const [servicesBreakdownOpen, setServicesBreakdownOpen] = useState(true);
	const [investigationsBreakdownOpen, setInvestigationsBreakdownOpen] = useState(true);

	const loadDoctorOptions = useCallback(async (inputValue) => {
		const search = inputValue?.trim();
		if (!search || search.length < 2) return [];

		try {
			const response = await masterPharmaDoctorsAPI({ search });
			const doctors = response?.data?.data || [];
			return doctors.map((doctor) => ({
				value: doctor._id,
				label: buildDoctorSelectOption(doctor).label,
				data: doctor,
			}));
		} catch (error) {
			console.error("Error searching doctors:", error);
			return [];
		}
	}, []);

	const doctorSelectStyles = useMemo(
		() => ({
			menuPortal: (base) => ({ ...base, zIndex: 10000 }),
			menu: (base) => ({ ...base, zIndex: 10000 }),
		}),
		[]
	);

	const handleDoctorSelect = (option) => {
		if (!option) {
			onClearDoctorSelection?.();
			return;
		}

		if (option.__isNew__ || option.isNew) {
			const name = sanitizeDoctorName(option.label || option.value);
			if (!name) {
				onClearDoctorSelection?.();
				return;
			}
			onDoctorIdChange?.(null);
			onSelectedDoctorOptionChange?.(buildNewDoctorOption(name));
			onDoctorFormChange?.({
				...EMPTY_DOCTOR_FORM,
				name,
			});
			return;
		}

		onSelectedDoctorOptionChange?.(option);
		onDoctorIdChange?.(option.value);
		onDoctorFormChange?.(mapMasterDoctorToForm(option.data));
	};

	const handleDoctorFieldChange = (field, value) => {
		if (doctorId && field !== "note") {
			onDoctorIdChange?.(null);
			onSelectedDoctorOptionChange?.(null);
		}
		onDoctorFormChange?.({ [field]: value });
		if (field === "name" && !doctorId) {
			onSelectedDoctorOptionChange?.(buildNewDoctorOption(value));
		}
	};

	const showAddDoctorButton = !doctorId && hasDoctorFormInput(doctorForm);
	const isDraftDoctor = !doctorId && Boolean(sanitizeDoctorName(doctorForm?.name));

	const prescription = activePrescription || {};
	const billingFlags = useMemo(
		() => getPrescriptionBillingFlags(prescription),
		[prescription]
	);
	const pharmacyCurrency = defaultCurrency || billingBaseCurrency || allowedCurrencies?.[0] || "INR";
	// All billing line items and summary amounts use base currency (same as Collect Payment).
	const displayCurrency = billingBaseCurrency || pharmacyCurrency;
	const paymentCurrency = payment.currency || displayCurrency;
	const displayCurrencySymbol = getCurrencySymbol(displayCurrency);

	const lineAmountInputProps = useMemo(
		() => ({
			displayCurrency,
			convertCurrency,
			pharmacyCurrency,
			allowedCurrencies,
			getCurrencySymbol,
		}),
		[
			allowedCurrencies,
			displayCurrency,
			convertCurrency,
			getCurrencySymbol,
			pharmacyCurrency,
		]
	);

	const getServiceItemValue = useCallback(
		(item) => item?.service_id || item?._id || item?.id,
		[]
	);
	const formatServiceOptionLabel = useCallback(
		(item) =>
			formatMasterOptionLabel(
				item?.service_name || item?.name,
				item?.price ?? item?.charges ?? item?.amount ?? 0,
				item?.currency,
				getCurrencySymbol,
				pharmacyCurrency,
				allowedCurrencies
			),
		[allowedCurrencies, getCurrencySymbol, pharmacyCurrency]
	);

	const getInvestigationItemValue = useCallback(
		(item) => item?.investigation_id || item?._id || item?.id || item?.test_id,
		[]
	);
	const formatInvestigationOptionLabel = useCallback(
		(item) =>
			formatMasterOptionLabel(
				item?.test_name || item?.name,
				item?.price ?? item?.charges ?? item?.amount ?? 0,
				item?.currency,
				getCurrencySymbol,
				pharmacyCurrency,
				allowedCurrencies
			),
		[allowedCurrencies, getCurrencySymbol, pharmacyCurrency]
	);

	const usedServiceIds = useMemo(
		() =>
			new Set(
				serviceItems
					.filter((item) => item.service_id)
					.map((item) => String(item.service_id))
			),
		[serviceItems]
	);

	const loadServiceMasterOptions = useCallback(
		async (inputValue) => {
			if (typeof searchGeneralServices !== "function") return [];
			const list = await searchGeneralServices(inputValue);
			return (list || []).slice(0, 80).map((masterItem) => {
				const id = String(getServiceItemValue(masterItem) || "");
				return {
					value: getServiceItemValue(masterItem),
					label: formatServiceOptionLabel(masterItem),
					data: masterItem,
					isDisabled: Boolean(id && usedServiceIds.has(id)),
				};
			});
		},
		[
			formatServiceOptionLabel,
			getServiceItemValue,
			searchGeneralServices,
			usedServiceIds,
		]
	);

	const loadLabMasterOptions = useCallback(
		async (inputValue) => {
			if (typeof searchLabTests !== "function") return [];
			const list = await searchLabTests(inputValue);
			return (list || []).slice(0, 80).map((masterItem) => ({
				value: getInvestigationItemValue(masterItem),
				label: formatInvestigationOptionLabel(masterItem),
				data: masterItem,
			}));
		},
		[
			formatInvestigationOptionLabel,
			getInvestigationItemValue,
			searchLabTests,
		]
	);

	const patientName =
		prescription?.patient?.name ||
		selectedPatient?.name ||
		selectedPatient?.full_name ||
		"-";
	const fileNumber =
		prescription?.patient?.health_id ||
		selectedPatient?.health_id ||
		"-";
	const prescriptionNumber =
		prescription?.appointment_invoice?.invoice_number || "-";
	const visitDate = formatDate(prescription?.createdAt);

	const pharmacyItemCount = (priorPharmacyItems?.length || 0) + cart.length;
	const servicesItemCount = serviceItems.length;
	const investigationsItemCount = investigationItems.length;

	const servicesTotal = billingSummary.services_total ?? 0;
	const investigationsTotal = billingSummary.investigation_total ?? 0;
	const { combinedFooter } = usePharmacyMedicinesDisplayModel({
		cart,
		priorPharmacyItems,
		itemTaxes,
		cartTotals: totals,
		discountType,
		discountValue,
		posTaxType,
		pharmacyStateCode,
		patientStateCode,
		showPosTax,
	});

	const toDisplay = useCallback(
		(amount) => {
			const value = toMoneyNumber(amount);
			if (displayCurrency === pharmacyCurrency) return value;
			return toMoneyNumber(convertCurrency(value, pharmacyCurrency, displayCurrency));
		},
		[convertCurrency, displayCurrency, pharmacyCurrency]
	);

	const lineDerivedGrand = useMemo(() => {
		return toMoneyNumber(
			(combinedFooter?.taxable ?? 0) -
				(combinedFooter?.discount ?? 0) +
				(combinedFooter?.tax ?? 0) +
				(totals?.transport ?? 0)
		);
	}, [combinedFooter, totals?.transport]);

	const pharmacyGrandTotal = useMemo(() => {
		const hasPharmacyItems = pharmacyItemCount > 0;
		if (hasPharmacyItems) {
			return toDisplay(lineDerivedGrand);
		}
		const fallbackVal = billingSummary.pharmacy_total ?? toDisplay(totals?.netTotal ?? 0);
		return toMoneyNumber(fallbackVal);
	}, [pharmacyItemCount, lineDerivedGrand, billingSummary.pharmacy_total, totals?.netTotal, toDisplay]);

	const pharmacySummaryTotals = useMemo(
		() => ({
			subtotal:
				billingSummary.pharmacy_subtotal ??
				billingSummary.pharmacy_taxable_total ??
				totals?.subtotal ??
				0,
			discount: billingSummary.pharmacy_discount ?? totals?.discount ?? 0,
			tax: billingSummary.pharmacy_tax ?? totals?.tax ?? 0,
			CGST: billingSummary.pharmacy_CGST ?? totals?.CGST ?? 0,
			SGST: billingSummary.pharmacy_SGST ?? totals?.SGST ?? 0,
			IGST: billingSummary.pharmacy_IGST ?? totals?.IGST ?? 0,
			VAT: billingSummary.pharmacy_VAT ?? totals?.VAT ?? 0,
			transport: billingSummary.transport ?? totals?.transport ?? 0,
			netTotal: billingSummary.pharmacy_total ?? totals?.netTotal ?? 0,
		}),
		[billingSummary, totals]
	);

	const isReadOnlyService = (item) =>
		item.isConsultation || (item.isPrescribed && !item.isNewRow);

	const isSelectableService = (item) =>
		!item.isConsultation && (item.isNewRow || item.source === "added");

	const isReadOnlyInvestigation = (item) => item.isPrescribed && !item.isNewRow;

	const isSelectableInvestigation = (item) =>
		item.isNewRow || item.source === "added";



	return (
		<Modal
			show={show}
			onHide={onHide}
			fullscreen="md-down"
			size="xl"
			scrollable
			centered
			backdrop="static"
			className="billing-payment-modal"
		>
			<Modal.Header closeButton={!isSubmitting} className="billing-modal-header">
				<Modal.Title>{t("Billing & Payment")}</Modal.Title>
			</Modal.Header>

			<Modal.Body className="billing-modal-body position-relative">
				{isLoading && (
					<div className="billing-modal-loader" aria-live="polite" aria-busy="true">
						<div className="billing-modal-loader-content">
							<i className="fa fa-spinner fa-spin billing-modal-loader-icon" />
							<span>{t("Loading billing details...")}</span>
						</div>
					</div>
				)}

				<div className={isLoading ? "billing-modal-content billing-modal-content--loading" : "billing-modal-content"}>
					<div className="billing-patient-banner billing-patient-banner--compact">
						<div className="billing-patient-banner-head">
							<span className="billing-patient-avatar">
								<i className="fa fa-user-circle" />
							</span>
							<div className="billing-patient-head-main">
								<div className="billing-patient-name">{patientName}</div>
								<div className="billing-patient-meta">
									{fileNumber !== "-" && (
										<span>
											<i className="fa fa-id-card-o me-1" />
											{fileNumber}
										</span>
									)}
									{visitDate && visitDate !== "-" && (
										<span>
											<i className="fa fa-calendar me-1" />
											{visitDate}
										</span>
									)}
									{prescriptionNumber !== "-" && (
										<span className="billing-patient-chip" title={t("Prescription")}>
											<span className="billing-patient-chip-label">{t("Rx")}</span>
											{prescriptionNumber}
										</span>
									)}
									{/* <span
										className="billing-patient-chip"
										title={displayCurrency}
									>
										{displayCurrencySymbol}
									</span> */}
								</div>
							</div>
						</div>
						<Row className="g-2 billing-doctor-row">
							<Col xs={12} lg={8}>
								<div className="billing-field-group">
									<div className="billing-field-label d-flex align-items-center justify-content-between gap-2">
										<span>{t("Prescribed Doctor")}</span>
										{doctorId ? (
											<span className="billing-doctor-linked-badge">{t("Linked")}</span>
										) : null}
									</div>
									<AsyncCreatableSelect
										key={selectedDoctorOption?.value || "billing-doctor-cleared"}
										inputId="billing-doctor-search"
										classNamePrefix="react-select"
										cacheOptions
										isClearable
										allowCreateWhileLoading={false}
										loadOptions={loadDoctorOptions}
										value={selectedDoctorOption}
										onChange={handleDoctorSelect}
										placeholder={t("Search by name, email or phone...")}
										formatCreateLabel={(inputValue) =>
											`${t("Use")} "${inputValue.trim()}"`
										}
										isValidNewOption={(inputValue, _value, options) => {
											const search = String(inputValue || "").trim();
											if (search.length < 2) return false;
											const existing = (options || []).filter((opt) => !opt.__isNew__);
											return existing.length === 0;
										}}
										noOptionsMessage={({ inputValue }) =>
											!inputValue || inputValue.length < 2
												? t("Type at least 2 characters")
												: t("No doctors found — use the name above to continue")
										}
										menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
										menuPosition="fixed"
										menuShouldScrollIntoView
										styles={doctorSelectStyles}
									/>
									{isDraftDoctor ? (
										<p className="billing-doctor-draft-hint small text-muted mb-1 mt-1">
											{t("This doctor will be created automatically when you generate the bill.")}
										</p>
									) : null}
									{showAddDoctorButton ? (
										<Button
											variant="outline-primary"
											size="sm"
											type="button"
											className="mt-1"
											disabled={addingDoctor}
											onClick={() => onAddDoctorToMaster?.()}
										>
											{addingDoctor ? t("Adding...") : t("Add Doctor to Master")}
										</Button>
									) : null}
								</div>
							</Col>
							<Col xs={12} lg={4}>
								<div className="billing-field-group">
									<div className="billing-field-label">{t("Note")}</div>
									<Form.Control
										as="textarea"
										rows={1}
										size="sm"
										className="billing-note-input"
										value={doctorForm.note || ""}
										placeholder={t("Additional notes")}
										onChange={(e) => handleDoctorFieldChange("note", e.target.value)}
										onFocus={disableScrollProps?.onFocus}
										onBlur={disableScrollProps?.onBlur}
										onKeyDown={disableScrollProps?.onKeyDown}
									/>
								</div>
							</Col>
						</Row>
					</div>

					<Row className="billing-layout g-3">
						<Col lg={8}>
							<div className="billing-sections-stack">
								<CollapsibleSection
									variant="pharmacy"
									title={t("Pharmacy Medicines")}
									total={pharmacyGrandTotal}
									currency={displayCurrency}
									isOpen={sectionOpen.medicines}
									onToggle={() => onToggleSection("medicines")}
									getCurrencySymbol={getCurrencySymbol}
									itemCount={pharmacyItemCount}
								>
									<div className="billing-table-wrap">
										<BillingPharmacyMedicinesTable
											t={t}
											cart={cart}
											priorPharmacyItems={priorPharmacyItems}
											itemTaxes={itemTaxes}
											totals={totals}
											discountType={discountType}
											discountValue={discountValue}
											posTaxType={posTaxType}
											pharmacyStateCode={pharmacyStateCode}
											patientStateCode={patientStateCode}
											displayCurrency={displayCurrency}
											pharmacyCurrency={pharmacyCurrency}
											convertCurrency={convertCurrency}
											getCurrencySymbol={getCurrencySymbol}
											showPosTax={showPosTax}
										/>
									</div>
								</CollapsibleSection>

								<CollapsibleSection
									variant="services"
									title={t("Clinic Consultation & Services")}
									total={servicesTotal}
									currency={displayCurrency}
									isOpen={sectionOpen.services}
									onToggle={() => onToggleSection("services")}
									getCurrencySymbol={getCurrencySymbol}
									itemCount={servicesItemCount}
								>
									<div className="table-responsive billing-table-wrap">
										<Table bordered size="sm" className="mb-2 billing-table services-table">
											<thead>
												<tr>
													<th scope="col">{t("Service")}</th>
													<th scope="col">{t("Quantity")}</th>
													<th scope="col" className="billing-col-amount">
														<span
															className="billing-col-amount-symbol"
															title={displayCurrency}
														>
															{displayCurrencySymbol}
														</span>
														<span className="billing-col-amount-label">{t("Amount")}</span>
													</th>
												</tr>
											</thead>
											<tbody>
												{serviceItems.length > 0 ? (
													serviceItems.map((item) => (
														<tr
															key={item.id}
															className={
																isSelectableService(item)
																	? "billing-row billing-row--editable"
																	: "billing-row billing-row--locked"
															}
														>
															<td>
																<div className="billing-line-name">
																	{isReadOnlyService(item) ? (
																		<>
																			<span className="billing-line-title">
																				{item.service_name}
																			</span>
																			{item.isConsultation && (
																				<SourceBadge variant="consultation">
																					{t("Consultation")}
																				</SourceBadge>
																			)}
																			{item.isPrescribed && !item.isConsultation && (
																				<SourceBadge variant="prescribed">
																					{t("Prescribed")}
																				</SourceBadge>
																			)}
																		</>
																	) : isSelectableService(item) ? (
																		<BillingMasterSelect
																			loadMasterOptions={loadServiceMasterOptions}
																			value={item.service_id || ""}
																			selectedLabel={item.service_name}
																			onChange={(nextId, opt) =>
																				onSelectServiceForRow(
																					item.id,
																					nextId,
																					opt?.data
																				)
																			}
																			placeholder={t("Select Service")}
																			noOptionsMessage={(inputValue) =>
																				!inputValue ||
																				String(inputValue).trim().length < 2
																					? t("Type at least 2 characters...")
																					: t("No services found")
																			}
																		/>
																	) : (
																		<span className="billing-line-title">
																			{item.service_name}
																		</span>
																	)}
																</div>
															</td>
															<td>
																<BillingLineQuantityInput
																	item={item}
																	updateFn={updateServiceQuantity}
																	disabled={isSelectableService(item) && !item.service_id}
																	disableScrollProps={disableScrollProps}
																/>
															</td>
															<td>
																<BillingAmountCell
																	onRemove={
																		isSelectableService(item)
																			? () => onRemoveServiceItem(item.id)
																			: null
																	}
																	removeLabel={t("Remove service")}
																>
																	<BillingLineAmountInput
																		item={item}
																		updateFn={updateServiceAmount}
																		disabled={isSelectableService(item) && !item.service_id}
																		{...lineAmountInputProps}
																		disableScrollProps={disableScrollProps}
																	/>
																</BillingAmountCell>
															</td>
														</tr>
													))
												) : (
													<tr>
														<td colSpan={3} className="text-center text-muted">
															{t("No services added")}
														</td>
													</tr>
												)}
											</tbody>
										</Table>
									</div>
									<Button
										variant="outline-primary"
										size="sm"
										className="billing-add-row-btn"
										onClick={onAddServiceRow}
									>
										<i className="fa fa-plus me-1" />
										{t("Add Service")}
									</Button>

									<BillingSectionDivider title={t("Discount & Tax")} />
									<BillingServicesAdjustments
										t={t}
										servicesBilling={servicesBillingTotals}
										servicesTaxConfig={servicesTaxConfig}
										servicesDiscountType={servicesDiscountType}
										servicesDiscountValue={servicesDiscountValue}
										onDiscountTypeChange={(type) =>
											onServicesDiscountChange({ discountType: type })
										}
										onDiscountValueChange={(value) =>
											onServicesDiscountChange({ discountValue: value })
										}
										onTaxConfigChange={onServicesTaxChange}
										displayCurrency={displayCurrency}
										getCurrencySymbol={getCurrencySymbol}
										posTaxType={posTaxType}
										pharmacyStateCode={pharmacyStateCode}
										patientStateCode={patientStateCode}
										disableScrollProps={disableScrollProps}
										showTax={billingFlags.showServicesTax}
									/>
								</CollapsibleSection>

								{billingFlags.showLabSection && (
									<CollapsibleSection
										variant="lab"
										title={t("Lab Investigations")}
										total={investigationsTotal}
										currency={displayCurrency}
										isOpen={sectionOpen.investigations}
										onToggle={() => onToggleSection("investigations")}
										getCurrencySymbol={getCurrencySymbol}
										itemCount={investigationsItemCount}
									>
										<SuggestedInvestigationsBlock
											suggestions={suggestedInvestigations}
											t={t}
										/>
										{suggestedInvestigations.length > 0 && investigationItems.length > 0 && (
											<div className="billing-section-divider-line" />
										)}
										<div className="table-responsive billing-table-wrap">
											<Table bordered size="sm" className="mb-2 billing-table investigations-table">
												<thead>
													<tr>
														<th scope="col">{t("Test Name")}</th>
														<th scope="col">{t("Quantity")}</th>
														<th scope="col" className="billing-col-amount">
															<span
																className="billing-col-amount-symbol"
																title={displayCurrency}
															>
																{displayCurrencySymbol}
															</span>
															<span className="billing-col-amount-label">{t("Amount")}</span>
														</th>
													</tr>
												</thead>
												<tbody>
													{investigationItems.length > 0 ? (
														investigationItems.map((item) => (
															<tr
																key={item.id}
																className={
																	isSelectableInvestigation(item)
																		? "billing-row billing-row--editable"
																		: "billing-row billing-row--locked"
																}
															>
																<td>
																	<div className="billing-line-name">
																		{isReadOnlyInvestigation(item) ? (
																			<>
																				<span className="billing-line-title">
																					{item.test_name}
																				</span>
																				<SourceBadge variant="prescribed">
																					{t("Prescribed")}
																				</SourceBadge>
																			</>
																		) : isSelectableInvestigation(item) ? (
																			<BillingMasterSelect
																				loadMasterOptions={loadLabMasterOptions}
																				value={item.test_id || ""}
																				selectedLabel={item.test_name}
																				onChange={(nextId, opt) =>
																					onSelectInvestigationForRow(
																						item.id,
																						nextId,
																						opt?.data
																					)
																				}
																				placeholder={t("Select Investigation")}
																				noOptionsMessage={(inputValue) =>
																					!inputValue ||
																					String(inputValue).trim().length < 2
																						? t("Type at least 2 characters...")
																						: t("No investigations found")
																				}
																			/>
																		) : (
																			<span className="billing-line-title">
																				{item.test_name}
																			</span>
																		)}
																	</div>
																</td>
																<td>
																	<BillingLineQuantityInput
																		item={item}
																		updateFn={updateInvestigationQuantity}
																		disabled={!item.test_id}
																		disableScrollProps={disableScrollProps}
																	/>
																</td>
																<td>
																	<BillingAmountCell
																		onRemove={() => onRemoveInvestigation(item.id)}
																		removeLabel={t("Remove investigation")}
																	>
																		<BillingLineAmountInput
																			item={item}
																			updateFn={updateInvestigationAmount}
																			disabled={!item.test_id}
																			{...lineAmountInputProps}
																			disableScrollProps={disableScrollProps}
																		/>
																	</BillingAmountCell>
																</td>
															</tr>
														))
													) : (
														<tr>
															<td colSpan={3} className="text-center text-muted">
																{t("No investigations added")}
															</td>
														</tr>
													)}
												</tbody>
											</Table>
										</div>
										<Button
											variant="outline-primary"
											size="sm"
											className="billing-add-row-btn"
											onClick={onAddInvestigationRow}
										>
											<i className="fa fa-plus me-1" />
											{t("Add Investigation")}
										</Button>

										<BillingSectionDivider title={t("Discount & Tax")} />
										<BillingInvestigationsAdjustments
											t={t}
											investigationsBilling={investigationsBillingTotals}
											investigationsTaxConfig={investigationsTaxConfig}
											investigationsDiscountType={investigationsDiscountType}
											investigationsDiscountValue={investigationsDiscountValue}
											onDiscountTypeChange={(type) =>
												onInvestigationsDiscountChange({ discountType: type })
											}
											onDiscountValueChange={(value) =>
												onInvestigationsDiscountChange({ discountValue: value })
											}
											onTaxConfigChange={onInvestigationsTaxChange}
											displayCurrency={displayCurrency}
											getCurrencySymbol={getCurrencySymbol}
											posTaxType={posTaxType}
											pharmacyStateCode={pharmacyStateCode}
											patientStateCode={patientStateCode}
											disableScrollProps={disableScrollProps}
											showTax={billingFlags.showLabTax}
										/>
									</CollapsibleSection>
								)}
							</div>
						</Col>

						<Col lg={4}>
							<div className="billing-summary-sticky">
								<div className="billing-summary-card mb-3">
									<h6 className="billing-summary-card-title mb-3">{t("Billing Summary")}</h6>

									<BillingPharmacySummaryBreakdown
										t={t}
										cart={cart}
										priorPharmacyItems={priorPharmacyItems}
										itemTaxes={itemTaxes}
										cartTotals={totals}
										totals={pharmacySummaryTotals}
										pharmacyTotal={pharmacyGrandTotal}
										discountType={discountType}
										discountValue={discountValue}
										posTaxType={posTaxType}
										pharmacyStateCode={pharmacyStateCode}
										patientStateCode={patientStateCode}
										displayCurrency={displayCurrency}
										pharmacyCurrency={pharmacyCurrency}
										convertCurrency={convertCurrency}
										getCurrencySymbol={getCurrencySymbol}
										isOpen={pharmacyBreakdownOpen}
										onToggle={() => setPharmacyBreakdownOpen((prev) => !prev)}
										showPosTax={showPosTax}
									/>

									<BillingServicesSummaryBreakdown
										t={t}
										billingSummary={billingSummary}
										servicesTaxConfig={servicesTaxConfig}
										posTaxType={posTaxType}
										pharmacyStateCode={pharmacyStateCode}
										patientStateCode={patientStateCode}
										displayCurrency={displayCurrency}
										getCurrencySymbol={getCurrencySymbol}
										isOpen={servicesBreakdownOpen}
										onToggle={() => setServicesBreakdownOpen((prev) => !prev)}
										showTax={billingFlags.showServicesTax}
									/>

									{billingFlags.showLabSection && (
										<BillingInvestigationsSummaryBreakdown
											t={t}
											billingSummary={billingSummary}
											investigationsTaxConfig={investigationsTaxConfig}
											posTaxType={posTaxType}
											pharmacyStateCode={pharmacyStateCode}
											patientStateCode={patientStateCode}
											displayCurrency={displayCurrency}
											getCurrencySymbol={getCurrencySymbol}
											isOpen={investigationsBreakdownOpen}
											onToggle={() => setInvestigationsBreakdownOpen((prev) => !prev)}
											showTax={billingFlags.showLabTax}
										/>
									)}

									<hr className="billing-summary-divider" />
									<div className="billing-grand-summary-box">
										<div className="summary-row grand-total-row">
											<span>{t("Grand Total")}</span>
											<strong>
												{formatAmount(billingSummary.grand_total, displayCurrency, getCurrencySymbol)}
											</strong>
										</div>
										<BillingPriorPaymentSummaryRows
											t={t}
											paymentBalance={paymentBalance}
											displayCurrency={displayCurrency}
											getCurrencySymbol={getCurrencySymbol}
										/>
									</div>
								</div>

								<div className="billing-payment-card">
									<h6 className="billing-payment-title fw-bold mb-3">{t("Payment")}</h6>
									<Row className="g-2">
										<Col sm={6}>
											<Form.Group>
												<Form.Label>
													{t("Currency")} <span className="text-danger">*</span>
												</Form.Label>
												<Form.Select
													value={payment.currency}
													onChange={(e) => onPaymentChange({ currency: e.target.value })}
													isInvalid={submitted && !!errors.currency}
													{...disableScrollProps}
												>
													{(allowedCurrencies?.length ? allowedCurrencies : [paymentCurrency]).map(
														(code) => (
															<option key={code} value={code} title={code}>
																({getCurrencySymbol(code)}) {code}
															</option>
														)
													)}
												</Form.Select>
												{submitted && errors.currency && (
													<div className="text-danger small mt-1">{errors.currency}</div>
												)}
											</Form.Group>
										</Col>
										<Col sm={6}>
											<Form.Group>
												<Form.Label>
													{t("Payment Method")} <span className="text-danger">*</span>
												</Form.Label>
												<Form.Select
													value={payment.payment_method}
													onChange={(e) =>
														onPaymentChange({ payment_method: e.target.value })
													}
													isInvalid={submitted && !!errors.payment_method}
													{...disableScrollProps}
												>
													{PAYMENT_METHOD_OPTIONS.map((option) => (
														<option key={option.value} value={option.value}>
															{t(option.label)}
														</option>
													))}
												</Form.Select>
												{submitted && errors.payment_method && (
													<div className="text-danger small mt-1">{errors.payment_method}</div>
												)}
											</Form.Group>
										</Col>
										<Col xs={12}>
											<Form.Group>
												<Form.Label>
													{paymentBalance.hasPriorPayment
														? t("Balance Due")
														: t("Grand Total")}
												</Form.Label>
												<div className="billing-grand-total-display">
													{formatAmount(
														paymentBalance.hasPriorPayment
															? paymentBalance.amountDue
															: paymentBalance.grandTotal,
														paymentCurrency,
														getCurrencySymbol
													)}
												</div>
												{paymentBalance.hasPriorPayment && (
													<small className="text-muted">
														{t("Invoice Total")}:{" "}
														{formatAmount(
															paymentBalance.grandTotal,
															paymentCurrency,
															getCurrencySymbol
														)}
														{" · "}
														{t("Paid")}:{" "}
														{formatAmount(
															paymentBalance.paidSoFar,
															paymentCurrency,
															getCurrencySymbol
														)}
													</small>
												)}
											</Form.Group>
										</Col>
										<Col xs={12}>
											<Form.Group>
												<Form.Label>
													{paymentBalance.hasPriorPayment
														? t("Amount to Collect")
														: t("Paid Amount")}{" "}
													<span className="text-danger">*</span>
												</Form.Label>
												<Form.Control
													type="text"
													value={payment.amount_received}
													onChange={(e) => {
														const value = e.target.value;
														if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
															onPaymentChange({ amount_received: value });
														}
													}}
													isInvalid={submitted && !!errors.amount_received}
													placeholder={t("Enter amount")}
													{...disableScrollProps}
												/>
												{submitted && errors.amount_received && (
													<div className="text-danger small mt-1">{errors.amount_received}</div>
												)}
											</Form.Group>
										</Col>
									</Row>

									<div className="billing-balance-box mt-2">
										<div
											className={`balance-row balance-row--status balance-row--${paymentBalance.status
												.toLowerCase()
												.replace(/[\s_]+/g, "-")}`}
										>
											<span>{t("Status")}</span>
											<strong>{t(paymentBalance.statusLabel)}</strong>
										</div>
										<div className="balance-row balance-row--pending">
											<span>{t("Balance")} ({paymentCurrency})</span>
											<strong>
												{formatAmount(
													paymentBalance.pending > 0 ? paymentBalance.pending : 0,
													paymentCurrency,
													getCurrencySymbol
												)}
											</strong>
										</div>
										{paymentBalance.showBaseCurrency && (
											<div className="balance-row balance-row--pending-alt">
												<span>{t("Balance")} ({billingBaseCurrency})</span>
												<strong>
													{formatAmount(
														paymentBalance.pendingBase > 0 ? paymentBalance.pendingBase : 0,
														billingBaseCurrency,
														getCurrencySymbol
													)}
												</strong>
											</div>
										)}
										{paymentBalance.change > 0 && (
											<div className="balance-row balance-row--change">
												<span>{t("Change Amount")} ({paymentCurrency})</span>
												<strong>
													{formatAmount(paymentBalance.change, paymentCurrency, getCurrencySymbol)}
												</strong>
											</div>
										)}
									</div>
								</div>
							</div>
						</Col>
					</Row>
				</div>
			</Modal.Body>

			<Modal.Footer className="billing-modal-footer flex-wrap gap-2">
				<Button variant="success" onClick={onCollectAndMarkPaid} disabled={isSubmitting || isLoading}>
					{t("Collect & Mark As Paid")}
				</Button>
				<Button variant="primary" onClick={onCollectPayment} disabled={isSubmitting || isLoading}>
					{t("Collect Payment")}
				</Button>
				<Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
					{t("Cancel")}
				</Button>
			</Modal.Footer>
		</Modal >
	);
};

export default BillingPaymentModal;
